import React from 'react';
import { Minus, Moon, Plus, Sun } from 'lucide-react';
import {
  MAX_CANVAS_SCALE,
  MIN_CANVAS_SCALE,
  ZOOM_STEP,
  useCanvasStore,
} from '../../store/canvasStore';
import { useEditorStore } from '../../store/editorStore';
import { SCALE_LABEL, pixelsToMetres, sqmToSqft } from '../../canvasConstants';

export type SaveStatus = 'saved' | 'saving' | 'local';

const SAVE_LABEL: Record<SaveStatus, string> = {
  saved: 'Saved',
  saving: 'Saving…',
  local: 'Saved on this device',
};

interface StatusBarProps {
  saveStatus?: SaveStatus;
}

const StatusBar: React.FC<StatusBarProps> = ({ saveStatus = 'saved' }) => {
  const { scale, shapes, zoomBy } = useCanvasStore();
  const { canvasTheme, toggleCanvasTheme } = useEditorStore();
  const zoomPct = Math.round(scale * 100);
  const sqm = shapes.reduce(
    (acc, s) => (s.type === 'room' ? acc + pixelsToMetres(s.width ?? 0) * pixelsToMetres(s.height ?? 0) : acc),
    0
  );

  return (
    <div
      className="flex items-center justify-between h-8 px-3 text-xs shrink-0"
      style={{ backgroundColor: '#1c1c28', borderColor: '#2e2e40', color: '#8888aa', borderTop: '1px solid #2e2e40' }}
    >
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-0.5">
          <button
            type="button"
            onClick={() => zoomBy(1 / ZOOM_STEP)}
            disabled={scale <= MIN_CANVAS_SCALE + 0.001}
            className="flex h-6 w-6 items-center justify-center rounded hover:bg-white/10 disabled:opacity-30"
            title="Zoom out"
            aria-label="Zoom out"
          >
            <Minus size={14} />
          </button>
          <span className="min-w-[4.5rem] text-center font-mono">Zoom {zoomPct}%</span>
          <button
            type="button"
            onClick={() => zoomBy(ZOOM_STEP)}
            disabled={scale >= MAX_CANVAS_SCALE - 0.001}
            className="flex h-6 w-6 items-center justify-center rounded hover:bg-white/10 disabled:opacity-30"
            title="Zoom in"
            aria-label="Zoom in"
          >
            <Plus size={14} />
          </button>
        </div>
        {sqm > 0 && <span className="font-mono">GIA {Math.round(sqmToSqft(sqm))} sq ft ({sqm.toFixed(1)} m²)</span>}
        <span style={{ color: saveStatus === 'saved' ? '#6dcc8a' : '#c8c8da' }}>{SAVE_LABEL[saveStatus]}</span>
      </div>
      <div className="flex items-center gap-3">
        <button
          onClick={toggleCanvasTheme}
          className="flex items-center gap-1.5 px-2 py-1 rounded hover:bg-white/5 transition-colors"
          title={canvasTheme === 'light' ? 'Switch to dark mode (sketching)' : 'Switch to light mode (print-style)'}
        >
          {canvasTheme === 'light' ? <Moon size={14} /> : <Sun size={14} />}
          <span>{canvasTheme === 'light' ? 'Light' : 'Dark'}</span>
        </button>
        <span>{SCALE_LABEL}</span>
      </div>
    </div>
  );
};

export default StatusBar;
